import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

export interface TabItem<T extends string> {
  value: T;
  label: string;
  /** Count shown as a small pill next to the label. */
  count?: number;
}

interface TabsProps<T extends string> {
  items: readonly TabItem<T>[];
  value: T;
  onChange: (value: T) => void;
  'aria-label': string;
  className?: string;
}

function nextIndex(key: string, index: number, length: number): number | null {
  switch (key) {
    case 'ArrowRight':
      return (index + 1) % length;
    case 'ArrowLeft':
      return (index - 1 + length) % length;
    case 'Home':
      return 0;
    case 'End':
      return length - 1;
    default:
      return null;
  }
}

/** Arrow/Home/End move between options (roving tabindex); handlers live on the buttons. */
function useRovingKeys<T extends string>(items: readonly TabItem<T>[], value: T, onChange: (value: T) => void) {
  const refs = useRef(new Map<string, HTMLButtonElement>());
  const onKeyDown = (event: KeyboardEvent): void => {
    const next = nextIndex(event.key, items.findIndex((item) => item.value === value), items.length);
    if (next === null) {
      return;
    }
    event.preventDefault();
    const target = items[next];
    if (target) {
      onChange(target.value);
      refs.current.get(target.value)?.focus();
    }
  };
  return { refs, onKeyDown };
}

/**
 * Underlined tabs for switching between views of the same page (Active / Leads / Moved out).
 * Keep to four or fewer so the strip never needs to scroll.
 */
export function Tabs<T extends string>({ items, value, onChange, className, ...rest }: TabsProps<T>) {
  const id = useId();
  const { refs, onKeyDown } = useRovingKeys(items, value, onChange);
  return (
    <div role="tablist" aria-label={rest['aria-label']} className={cn('flex gap-1 border-b border-slate-200', className)}>
      {items.map((item) => {
        const selected = item.value === value;
        return (
          <button
            key={item.value}
            ref={(node) => {
              if (node) {
                refs.current.set(item.value, node);
              }
            }}
            id={`${id}-${item.value}`}
            type="button"
            role="tab"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(item.value)}
            onKeyDown={onKeyDown}
            className={cn(
              '-mb-px inline-flex items-center gap-2 border-b-2 px-3 pb-3 pt-1 text-sm font-semibold transition-colors',
              selected ? 'border-brand-700 text-brand-800' : 'border-transparent text-slate-500 hover:text-slate-800',
            )}
          >
            {item.label}
            {item.count !== undefined && (
              <span className={cn('rounded-full px-1.5 py-0.5 text-2xs font-semibold tabular', selected ? 'bg-brand-100 text-brand-800' : 'bg-slate-100 text-slate-600')}>
                {item.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** Compact pill switcher for filtering within a card (All / Overdue / Paid). */
export function SegmentedControl<T extends string>({ items, value, onChange, className, ...rest }: TabsProps<T>) {
  const { refs, onKeyDown } = useRovingKeys(items, value, onChange);
  return (
    <div role="radiogroup" aria-label={rest['aria-label']} className={cn('inline-flex rounded-lg bg-slate-100 p-0.5', className)}>
      {items.map((item) => {
        const selected = item.value === value;
        return (
          <button
            key={item.value}
            ref={(node) => {
              if (node) {
                refs.current.set(item.value, node);
              }
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(item.value)}
            onKeyDown={onKeyDown}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors',
              selected ? 'bg-white text-slate-900 shadow-card' : 'text-slate-600 hover:text-slate-900',
            )}
          >
            {item.label}
            {item.count !== undefined && <span className="tabular text-slate-500">{item.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

export function TabPanel({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div role="tabpanel" className={className}>
      {children}
    </div>
  );
}
